import React, {useEffect, useState} from "react";
import {useParams, Link} from "react-router-dom";
import api, {resolveImage} from "../lib/api";
import {useSettings} from "../context/SettingsContext";
import Seo from "../components/site/Seo";
import Html from "../components/site/Html";
import * as Icons from "lucide-react";
import {ArrowRight, ExternalLink, Check} from "lucide-react";
import {Swiper, SwiperSlide} from "swiper/react";
import {Navigation, Pagination, Autoplay} from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const Icon = ({name, ...props}) => {
    const C = Icons[name] || Icons.Sparkles;
    return <C {...props} />;
};

const ProductDetail = () => {
    const {slug} = useParams();
    const {settings} = useSettings();
    const [p, setP] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setLoading(true);
        setNotFound(false);
        api.get(`/products/${slug}`)
            .then((r) => setP(r.data))
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [slug]);

    if (loading) {
        return <div className="py-32 text-center text-zinc-500" data-testid="product-loading">Loading...</div>;
    }
    if (notFound || !p) {
        return (<div className="py-32 text-center" data-testid="product-not-found">
            <h2 className="font-display text-3xl font-bold mb-3">Product not found</h2>
            <Link to="/products" className="text-orange-600 font-semibold">← Back to products</Link>
        </div>);
    }

    const isGreen = p.color === "green";
    const accentBg = isGreen ? "bg-green-50" : "bg-orange-50";
    const accentText = isGreen ? "text-green-700" : "text-orange-700";
    const accentBtn = isGreen ? "bg-green-500 hover:bg-green-600" : "bg-orange-500 hover:bg-orange-600";

    return (<div data-testid="product-detail-page">
        <Seo
            title={p.seo?.title || `${p.name} — Reptile Global`}
            description={p.seo?.description || p.short_description}
            keywords={p.seo?.keywords}
            ogImage={p.seo?.og_image}
            ogType={p.seo?.og_type || "product"}
            ogUrl={p.seo?.og_url}
            twitterCard={p.seo?.twitter_card}
            canonicalUrl={p.seo?.canonical_url}
            jsonLd={p.seo?.json_ld}
            organizationSchema={settings?.organization_schema}
        />

        {/* Hero */}
        <section className="relative overflow-hidden">
            <div
                className="absolute -top-24 -right-24 w-96 h-96 bg-orange-100 rounded-full blur-3xl pointer-events-none"/>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 relative">
                <Link to="/products" className="text-sm text-zinc-500 hover:text-orange-600 mb-6 inline-block">← All
                    products</Link>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
              <span
                  className={`text-xs uppercase tracking-[0.2em] font-bold px-3 py-1 rounded-full ${accentBg} ${accentText}`}>
                {p.category}
              </span>
                        <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-zinc-950 leading-[0.95]">
                            {p.name}
                        </h1>
                        <p className="mt-4 font-display text-2xl sm:text-3xl font-semibold text-zinc-700 tracking-tight">
                            {p.tagline}
                        </p>
                        <div className="mt-6 text-lg text-zinc-700 leading-relaxed max-w-xl"
                             data-testid="product-short-description">
                            {p.short_description ? <Html html={p.short_description}/> : null}
                        </div>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link to="/contact" data-testid="product-cta-demo"
                                  className={`${accentBtn} text-white px-8 py-3.5 rounded-full font-semibold inline-flex items-center gap-2 transition-colors`}>
                                Book a Demo <ArrowRight size={18}/>
                            </Link>
                            {p.external_url && (<a href={p.external_url} target="_blank" rel="noreferrer"
                                                   className="border-2 border-zinc-200 hover:border-orange-500 text-zinc-900 px-8 py-3.5 rounded-full font-semibold inline-flex items-center gap-2 transition-colors">
                                Visit website <ExternalLink size={16}/>
                            </a>)}
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div
                            className={`relative rounded-3xl border border-zinc-200 p-8 bg-white shadow-xl shadow-orange-500/5`}>
                            <div
                                className={`absolute -top-6 -left-6 w-16 h-16 rounded-2xl flex items-center justify-center text-white ${accentBtn}`}>
                                <Icon name={p.icon} size={28}/>
                            </div>
                            <div className="mt-6 space-y-3">
                                <div className="text-xs uppercase tracking-widest font-bold text-zinc-500">Product
                                    Highlights
                                </div>
                                {(p.features || []).slice(0, 4).map((f, i) => (
                                    <div key={i} className="flex items-start gap-3">
                                        <div
                                            className={`mt-1 w-5 h-5 rounded-full flex items-center justify-center ${isGreen ? "bg-green-100 text-green-600" : "bg-orange-100 text-orange-600"}`}>
                                            <Check size={12} strokeWidth={3}/>
                                        </div>
                                        <div>
                                            <div className="font-semibold text-zinc-900 text-sm">{f.title}</div>
                                            <div className="text-xs text-zinc-500">{f.description}</div>
                                        </div>
                                    </div>))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        {/* Long description (overview) */}
        {p.long_description && (<section className="py-16 sm:py-20 border-t border-zinc-100"
                                         data-testid="product-long-description-section">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-3">Overview</div>
                <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-zinc-950 mb-8">
                    About {p.name}
                </h2>
                <div className="text-lg text-zinc-700 leading-relaxed" data-testid="product-long-description">
                    <Html html={p.long_description}/>
                </div>
            </div>
        </section>)}

        {/* Features */}
        {(p.features || []).length > 0 && (<section className="py-20 bg-zinc-50/60">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-3">Features</div>
                <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-zinc-950">Built
                    for the way you work.</h2>
                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {p.features.map((f, i) => (<div key={i}
                                                    className="bg-white border border-zinc-200 rounded-2xl p-7 hover:-translate-y-1 hover:shadow-md transition-all">
                        <div
                            className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${isGreen ? "bg-green-100 text-green-600" : "bg-orange-100 text-orange-600"}`}>
                            <Icon name={f.icon} size={22}/>
                        </div>
                        <div className="font-display font-bold text-zinc-900 text-lg mb-2">{f.title}</div>
                        <div className="text-sm text-zinc-600 leading-relaxed">{f.description}</div>
                    </div>))}
                </div>
            </div>
        </section>)}

        {/* Screenshots */}
        {(p.screenshots || []).length > 0 && (<section className="py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="font-display text-4xl font-black tracking-tighter mb-10">
                    A look inside
                </h2>

                <Swiper
                    modules={[Autoplay]}
                    spaceBetween={20}
                    loop={true}
                    autoplay={{
                        delay: 3000,
                        disableOnInteraction: false,
                    }}
                    breakpoints={{
                        0: {
                            slidesPerView: 1,
                        },
                        768: {
                            slidesPerView: 2,
                        },
                        1024: {
                            slidesPerView: 3,
                        },
                    }}
                >
                    {p.screenshots.map((s, i) => (
                        <SwiperSlide key={i}>
                            <div className="rounded-2xl overflow-hidden border border-zinc-200 max-w-xs mx-auto">
                                <img
                                    src={resolveImage(s)}
                                    alt={`${p.name} screenshot ${i + 1}`}
                                    className="w-full h-[400px] object-contain bg-zinc-50"
                                />
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        </section>)}

        {/* Pricing */}
        {(p.pricing || []).length > 0 && (<section className="py-20 bg-zinc-50/60">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-3">
                    Pricing
                </div>

                <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-zinc-950">
                    Simple, transparent plans.
                </h2>

                <div className="mt-12">
                    <Swiper
                        modules={[Autoplay]}
                        spaceBetween={24}
                        autoplay={{
                            delay: 4000, disableOnInteraction: false,
                        }}
                        breakpoints={{
                            0: {
                                slidesPerView: 1,
                            }, 768: {
                                slidesPerView: 2,
                            }, 1200: {
                                slidesPerView: 3,
                            },
                        }}
                    >
                        {p.pricing.map((plan, i) => (<SwiperSlide key={i}>
                            <div
                                className={`rounded-2xl p-8 border h-full ${plan.highlighted ? "border-orange-500 bg-white shadow-xl shadow-orange-500/10" : "bg-white border-zinc-200"} relative`}
                            >
                                {plan.highlighted && ( <div className="mb-4 inline-block bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                                    MOST POPULAR
                                </div>)}

                                <div className="font-display font-bold text-zinc-900 text-lg">
                                    {plan.name}
                                </div>

                                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-display font-black text-4xl text-zinc-950">
                    {plan.price}
                  </span>
                                    <span className="text-zinc-500">{plan.period}</span>
                                </div>

                                <ul className="mt-6 space-y-2.5">
                                    {(plan.features || []).map((f, j) => (<li
                                        key={j}
                                        className="flex items-start gap-2 text-sm text-zinc-700"
                                    >
                                        <Check
                                            size={16}
                                            className="text-green-500 mt-0.5 flex-shrink-0"
                                        />
                                        {f}
                                    </li>))}
                                </ul>

                                <Link
                                    to="/contact"
                                    className={`mt-8 block text-center font-semibold rounded-full py-3 ${plan.highlighted ? "bg-orange-500 text-white hover:bg-orange-600" : "border-2 border-zinc-200 text-zinc-900 hover:border-orange-500"} transition-colors`}
                                >
                                    {plan.cta_label}
                                </Link>
                            </div>
                        </SwiperSlide>))}
                    </Swiper>
                </div>
            </div>
        </section>)}
        {/* FAQs */}
        {(p.faqs || []).length > 0 && (<section className="py-20">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="font-display text-4xl font-black tracking-tighter mb-10">Frequently asked
                    questions</h2>
                <div className="space-y-3">
                    {p.faqs.map((q, i) => (<details key={i}
                                                    className="group bg-white border border-zinc-200 rounded-xl p-5 cursor-pointer">
                        <summary
                            className="flex items-center justify-between font-semibold text-zinc-900 list-none">
                            {q.question}
                            <span
                                className="text-orange-500 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                        </summary>
                        <p className="mt-3 text-zinc-600 text-sm leading-relaxed">{q.answer}</p>
                    </details>))}
                </div>
            </div>
        </section>)}

        {/* CTA */}
        <section className="py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="rounded-3xl bg-zinc-950 px-8 sm:px-16 py-16 text-center relative overflow-hidden">
                    <div className="absolute -top-16 -right-16 w-80 h-80 bg-orange-500/30 rounded-full blur-3xl"/>
                    <h3 className="relative font-display text-3xl sm:text-4xl font-black tracking-tighter text-white">
                        {p.cta_title || `Ready to try ${p.name}?`}
                    </h3>
                    <p className="relative mt-4 text-zinc-400 text-lg max-w-xl mx-auto">
                        {p.cta_description || "Talk to our team and get a personalised demo."}
                    </p>
                    <Link to="/contact" data-testid="product-cta-final"
                          className={`relative mt-8 inline-flex ${accentBtn} text-white px-8 py-3.5 rounded-full font-semibold`}>
                        {p.cta_button_label || "Get Started"} →
                    </Link>
                </div>
            </div>
        </section>
    </div>);
};

export default ProductDetail;
